import { createFileRoute } from "@tanstack/react-router";
import { Check, Clock, Compass, ShieldCheck } from "lucide-react";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { FitCheck } from "@/components/site/FitCheck";
import { RiskRemoval } from "@/components/site/RiskRemoval";
import { ContactForm } from "@/components/site/ContactForm";
import { Reveal } from "@/components/site/wow/Reveal";
import { LivePulseBadge } from "@/components/site/wow/LivePulseBadge";
import { VideoEmbed } from "@/components/site/VideoEmbed";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { featuredCase, caseItems } from "@/content/cases";
import { aeoGeo } from "@/lib/seo/schema";

const SITE_ORIGIN = "https://fenny.se";
const PILOT_URL = `${SITE_ORIGIN}/pilot`;

const PILOT_FAQ = [
  {
    q: "Vad är ett Pilot?",
    a: "En avgränsad första produktion till fast pris. Ni ser exakt hur vi jobbar — tempo, kvalitet och samarbete — innan ni bestämmer er för ett löpande Pulse-abonnemang.",
  },
  {
    q: "Binder vi oss till något?",
    a: "Nej. Pilot är en engångsproduktion. Gillar ni resultatet växlar vi upp till Pulse. Gör ni inte det är vi inte sura — ni äger ändå materialet.",
  },
  {
    q: "Hur snabbt får vi första klippet?",
    a: "Normalt inom 10–14 dagar från att vi satt briefen. Pilot är byggt för att visa fart från start.",
  },
  {
    q: "Vad kostar ett Pilot?",
    a: "Ett Pilot går på 28 995 kr — fast pris, ingen bindning. Det inkluderar brief, en inspelningsdag, klipp och en huvudfilm plus två sociala cutdowns. Vill ni ha större omfattning sätter vi ett fast pris utifrån brief.",
  },
  {
    q: "Vad händer efter Pilot?",
    a: "Ingenting automatiskt. Ni äger materialet oavsett. Vill ni fortsätta växlar vi upp till Pulse — där räknar vi av Pilot-priset från första månaden.",
  },
];

export const Route = createFileRoute("/pilot")({
  head: () => ({
    meta: [
      { title: "Pilot — testa Fenny utan att binda er | Fenny" },
      {
        name: "description",
        content:
          "Pilot är Fennys lågtröskel-ingång: en avgränsad första filmproduktion till fast pris. Se tempo och kvalitet innan ni går vidare till abonnemang.",
      },
      { property: "og:title", content: "Pilot — testa Fenny utan att binda er" },
      {
        property: "og:description",
        content: "En avgränsad första produktion till fast pris. Inget långt avtal.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/pilot" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Pilot — testa Fenny utan att binda er" },
      { name: "twitter:description", content: "En avgränsad första produktion till fast pris." },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/pilot" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/pilot", name: "Pilot — testa Fenny utan att binda er | Fenny", description: "Pilot är Fennys lågtröskel-ingång: en avgränsad första filmproduktion till fast pris. Se tempo och kvalitet innan ni går vidare till abonnemang."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: PILOT_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: `${SITE_ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "Pilot", item: PILOT_URL },
          ],
        }),
      },
    ],
  }),
  component: PilotPage,
});

const steps = [
  {
    icon: Compass,
    title: "1. Kort brief",
    body: "30 minuter där vi ringar in ett konkret, avgränsat första uppdrag — inte en hel strategi.",
  },
  {
    icon: Clock,
    title: "2. Produktion på 14 dagar",
    body: "Vi spelar in och klipper. Ni ser senior kvalitet och vårt tempo i praktiken.",
  },
  {
    icon: ShieldCheck,
    title: "3. Ni bestämmer",
    body: "Gillar ni det växlar vi upp till Pulse. Annars äger ni materialet — utan bindning.",
  },
];
function PilotPage() {
  useScrollDepth("pilot");
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-12 md:pt-44 md:pb-16">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_30%_0%,color-mix(in_oklab,var(--mint)_16%,transparent),transparent_70%)]"
        />
        <div className="container-wide">
          <div className="max-w-3xl">
            <Reveal>
              <LivePulseBadge tone="mint" className="mb-6">
                Lågtröskel-ingång
              </LivePulseBadge>
            </Reveal>
            <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
              Testa oss med ett <span className="text-mint">Pilot</span> — innan ni binder er.
            </h1>
            <p className="mt-8 max-w-2xl text-pretty text-lg text-foreground/85 md:text-xl">
              En avgränsad första produktion till <strong>fast pris — 28&nbsp;995&nbsp;kr</strong>,
              ingen bindning. Ni ser exakt hur vi jobbar — tempo, kvalitet och samarbete — och
              bestämmer sedan om ni vill växla upp till ett löpande Pulse-abonnemang.
            </p>
            <ul className="mt-6 grid gap-2 text-sm text-foreground/85 md:grid-cols-2">
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-mint" /> Fast pris 28 995 kr — inga överraskningar</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-mint" /> Ingen bindning — engångsproduktion</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-mint" /> Första klippet inom 10–14 dagar</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-mint" /> 1 huvudfilm + 2 sociala cutdowns</li>
            </ul>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                to="/kontakt"
                variant="mint"
                arrow
                trackId="book_pilot"
                trackProps={{ location: "pilot_hero", label: "Starta ett Pilot" }}
              >
                Starta ett Pilot — 28 995 kr
              </Button>
              <Button
                to="/priser"
                variant="outline"
                trackId="view_pricing"
                trackProps={{ location: "pilot_hero", label: "Se priser" }}
              >
                Se priser
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Hur Pilot funkar */}
      <Section>
        <EyebrowHeading
          eyebrow="Så funkar ett Pilot"
          title="Tre steg, inga åtaganden."
          lede="Pilot är byggt för att ta bort tröskeln att börja. Ni riskerar inte ett långt avtal — bara en avgränsad produktion."
        />
        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {steps.map((s) => (
            <article key={s.title} className="flex flex-col rounded-3xl border border-white/10 bg-surface p-7">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-mint/15 text-mint ring-1 ring-mint/30">
                <s.icon className="h-5 w-5" strokeWidth={2} />
              </div>
              <h3 className="mt-5 text-lg font-semibold leading-tight">{s.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
            </article>
          ))}
        </div>
      </Section>

      {/* Dag för dag */}
      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Tidslinje"
          title="Dag för dag — 14 dagar från brief till klipp."
          lede="Så här ser en typisk Pilot ut. Vi flyttar dagar om det passar er kalender bättre."
        />
        <ol className="mt-10 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {[
            { day: "Dag 0–1", title: "Brief & scope", body: "30 min-samtal. Vi låser mål, målgrupp och format. Fast pris bekräftas." },
            { day: "Dag 2–4", title: "Pre-production", body: "Story, shotlist och logistik. Ni godkänner riktning innan kamerorna åker fram." },
            { day: "Dag 5–7", title: "Inspelningsdag", body: "En dag på plats. Senior fotograf, ljud och regi. Ni behöver inte producera något själva." },
            { day: "Dag 8–14", title: "Klipp & leverans", body: "Grovklipp dag 10, feedback, finklipp. Huvudfilm + 2 cutdowns levereras dag 14." },
          ].map((s) => (
            <li key={s.day} className="rounded-3xl border border-white/10 bg-background/40 p-6">
              <div className="text-xs uppercase tracking-widest text-mint">{s.day}</div>
              <div className="mt-2 text-base font-semibold">{s.title}</div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* Exempel på Pilot-filmer */}
      <Section>
        <EyebrowHeading
          eyebrow="Exempel"
          title="Så här kan ett Pilot se ut."
          lede="Filmer producerade i samma format och tempo som ett Pilot. Er film blir förstås er egen."
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          <div>
            <VideoEmbed id={featuredCase.id} title={featuredCase.title} />
            <div className="mt-3 text-sm text-muted-foreground">
              <span className="text-foreground/90 font-medium">{featuredCase.client}</span> — {featuredCase.excerpt}
            </div>
          </div>
          {caseItems[0] && (
            <div>
              <VideoEmbed id={caseItems[0].id} title={caseItems[0].title} />
              <div className="mt-3 text-sm text-muted-foreground">
                <span className="text-foreground/90 font-medium">{caseItems[0].client}</span> — {caseItems[0].excerpt}
              </div>
            </div>
          )}
        </div>
      </Section>



      {/* Fit check */}
      <FitCheck
        eyebrow="Är Pilot rätt för er?"
        title="Ett Pilot passar när ni vill se bevis först."
        fits={[
          "Ni är nyfikna på abonnemang men vill testa innan ni binder er",
          "Ni vill se vårt tempo och vår kvalitet på ett riktigt uppdrag",
          "Ni har ett konkret första filmbehov att starta med",
        ]}
        noFits={[
          "Ni vet redan att ni vill ha löpande flöde — gå direkt på Pulse",
          "Ni behöver en stor kampanj eller brandfilm — se Prime i stället",
        ]}
        tone="mint"
      />

      {/* Riskreducering */}
      <Reveal>
        <RiskRemoval />
      </Reveal>

      {/* FAQ */}
      <Section className="bg-surface/40">
        <div className="mx-auto max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-mint">Vanliga frågor</div>
          <h2 className="mt-4 text-balance text-3xl font-semibold md:text-5xl">Om Pilot</h2>
          <div className="mt-8 divide-y divide-white/10 rounded-3xl border border-white/10 bg-surface/40 backdrop-blur-md">
            {PILOT_FAQ.map((f) => (
              <details key={f.q} className="group p-6 md:p-7">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold">
                  <span>{f.q}</span>
                  <span className="mt-1 text-mint transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </Section>

      {/* Form */}
      <ContactForm location="pilot" defaultIntent="pulse" />
    </div>
  );
}
